// Surface rules (GEN-5): paint a column from the generated height + biome. Body
// is stone, deepslate below a noisy ~y0 line; then optional `under` (sandstone),
// `fillerDepth` filler, and the top block; water fills up to sea level; snow caps
// mountains above the snow line; a noise-stepped bedrock floor overwrites y −64..−60.
import { WORLD } from '../../config';
import { Simplex } from '../../engine/noise';
import { STATE_AIR } from '../../engine/section';
import type { Biome } from '../../registry/schemas';

export type SetFn = (lx: number, y: number, lz: number, state: number) => void;
export type Resolve = (name: string) => number;

/** Depth below sea level past which a seabed reads as "deep" (gravel, not sand). */
const SHALLOW_DEPTH = 4;
/** Above this, the clay-patch noise wins — clay sits in shallow water only. */
const CLAY_THRESHOLD = 0.62;

export class SurfacePainter {
  private readonly dsNoise: Simplex;
  private readonly brNoise: Simplex;
  private readonly seabedNoise: Simplex;
  private readonly stone: number;
  private readonly deepslate: number;
  private readonly bedrock: number;
  private readonly water: number;
  private readonly snow: number;
  private readonly sand: number;
  private readonly gravel: number;
  private readonly clay: number;

  constructor(seed: number | string | bigint, private readonly resolve: Resolve) {
    this.dsNoise = new Simplex(`${seed}#deepslate`);
    this.brNoise = new Simplex(`${seed}#bedrock`);
    this.seabedNoise = new Simplex(`${seed}#seabed`);
    this.stone = resolve('stone');
    this.deepslate = resolve('deepslate');
    this.bedrock = resolve('bedrock');
    this.water = resolve('water');
    this.snow = resolve('snow_block');
    this.sand = resolve('sand');
    this.gravel = resolve('gravel');
    this.clay = resolve('clay');
  }

  /** The seabed block for a land biome that happens to dip below sea level.
   *  Without this the column fell back to the biome filler — i.e. a lake floor
   *  of dirt. Shallows are sand with clay patches; deeper water is gravel. */
  private seabed(wx: number, wz: number, depth: number): number {
    if (depth > SHALLOW_DEPTH) return this.gravel;
    const n = (this.seabedNoise.noise2(wx * 0.08, wz * 0.08) + 1) / 2;
    return n > CLAY_THRESHOLD ? this.clay : this.sand;
  }

  paint(set: SetFn, bx: number, bz: number, lx: number, lz: number, height: number, biome: Biome): void {
    const wx = bx + lx, wz = bz + lz;
    const s = biome.surface;
    const underwater = height < WORLD.SEA_LEVEL;
    const fillerState = this.resolve(s.filler);
    let topState = this.resolve(s.top);
    // No grass underwater. A dedicated water biome (ocean/river/beach) already
    // names a sensible seabed, so only land biomes dipping below sea level get
    // the generated one — they used to end up with a dirt lake floor.
    if (underwater && s.top === 'grass_block') topState = this.seabed(wx, wz, WORLD.SEA_LEVEL - height);
    if (biome.snowLine != null && !underwater && height >= biome.snowLine) topState = this.snow;

    const underDepth = s.under ? s.underDepth ?? 0 : 0;
    const underState = s.under ? this.resolve(s.under) : STATE_AIR;
    const fillerBottom = height - s.fillerDepth; // filler occupies [fillerBottom, height-1]
    const underBottom = fillerBottom - underDepth; // under occupies [underBottom, fillerBottom-1]

    // Deepslate/stone body up to the under/filler zone.
    const deepslateLine = Math.floor(-4 + this.dsNoise.noise2(wx * 0.06, wz * 0.06) * 6);
    for (let y = WORLD.MIN_Y; y < underBottom; y++) {
      set(lx, y, lz, y < deepslateLine ? this.deepslate : this.stone);
    }
    if (underDepth > 0) {
      for (let y = Math.max(WORLD.MIN_Y, underBottom); y < fillerBottom; y++) set(lx, y, lz, underState);
    }
    for (let y = Math.max(WORLD.MIN_Y, fillerBottom); y < height; y++) set(lx, y, lz, fillerState);
    if (height >= WORLD.MIN_Y) set(lx, height, lz, topState);

    if (underwater) {
      for (let y = height + 1; y <= WORLD.SEA_LEVEL; y++) set(lx, y, lz, this.water);
    }

    // Bedrock floor: y=-64 solid, thinning to y=-60.
    for (let y = WORLD.MIN_Y; y <= WORLD.MIN_Y + 4; y++) {
      const p = 1 - (y - WORLD.MIN_Y) / 5;
      const n = (this.brNoise.noise2(wx * 0.37 + y * 5.1, wz * 0.37) + 1) / 2;
      if (n < p) set(lx, y, lz, this.bedrock);
    }
  }
}
